import React from "react";
import Spoiler from "../components/Spoiler";
import emojiRegex from "emoji-regex";
import { TextFormats } from "./global-variables";
import { v4 as uuidv4 } from "uuid";

export function entitiesAreDifferent(entities1 = [], entities2 = []) {
  if (entities1.length !== entities2.length) {
      return true;
  }

  for (let i = 0; i < entities1.length; i++) {
      const entity1 = entities1[i];
      const entity2 = entities2[i];

      if (entity1.offset !== entity2.offset ||
          entity1.length !== entity2.length ||
          entity1.type !== entity2.type) {
          return true;
      }
  }
  return false;
}

export const areObjectsEqual = (obj1, obj2) => {
  if (obj1 && obj2 && typeof obj1 === "object" && typeof obj2 === "object") {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (
      keys1.length === keys2.length &&
      keys1.every((key) => keys2.includes(key))
    ) {
      return keys1.every((key) => obj1[key] === obj2[key]);
    }
  }
  return false;
}
export function getNodeAndOffset(root, caretOffset) {
  let node = root;
  let nodeStack = [root];
  let offset = 0;

  while (nodeStack.length > 0) {
    node = nodeStack.pop();

    if (node.nodeType === Node.TEXT_NODE) {
      const textContent = node.textContent;
      const textLength = textContent.length;

      if (offset + textLength >= caretOffset) {
        return { node, offset: caretOffset - offset };
      } else {
        offset += textLength;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const childNodes = node.childNodes;

      for (let i = childNodes.length - 1; i >= 0; i--) {
        nodeStack.push(childNodes[i]);
      }
    }
  }

  // If caretOffset is beyond the end of the root node, return the end position
  return { node, offset: Math.max(0, offset - 1) };
}

export const isEntityInside = (entity1, entity2) => {
  const { offset: offset1, length: length1 } = entity1;
  const { offset: offset2, length: length2 } = entity2;

  return (
    entity1.type === entity2.type &&
    offset1 <= offset2 &&
    offset1 + length1 >= offset2 + length2
  );
}

export const isNewEntityPartOfExisting = (entity1, entity2) => {
  if (entity1.type === entity2.type) {
    if ((entity1.offset <= entity2.offset && entity1.offset + entity1.length >= entity2.offset + entity2.length)) {
      return true;
    }
  }
  return false;
}

const getIntersectionLength = (entity1, entity2) => {
  const entity1Start = entity1.offset;
  const entity1End = entity1.offset + entity1.length;
  const entity2Start = entity2.offset;
  const entity2End = entity2.offset + entity2.length;

  const intersectionStart = Math.max(entity1Start, entity2Start);
  const intersectionEnd = Math.min(entity1End, entity2End);

  const intersectionLength = Math.max(0, intersectionEnd - intersectionStart);
  return intersectionLength;
}

export const isRequiresModification = (entity1, entity2) => {
  if (entity1.type === entity2.type) {
    const intersection = findIntersection(entity1, entity2);
    return intersection !== -1;
  }
  return false;
}

export const getModifiedEntity = (entity1, entity2, entityExists, nextEntity) => {
  if (entityExists) {
    return {
      type: entity1.type,
      offset: entity2.offset + entity2.length,
      length: (entity1.offset + entity1.length) - (entity2.offset + entity2.length)
    }
  } else {
    const modifiedEntity = {
      type: entity1.type,
      offset: Math.min(entity1.offset, entity2.offset),
      length: entity1.length + entity2.length - getIntersectionLength(entity1, entity2)
    }
    if (!nextEntity) {
      return modifiedEntity;
    }
    const intersection = findIntersection(modifiedEntity, nextEntity);
    if (nextEntity &&  intersection !== -1) {
      return {
        ...modifiedEntity,
        length: nextEntity.offset
      }

    } else {
      return modifiedEntity;
    }
  }
}

const getTopLevelEntities = (sortedEntities) => {
  const topLevelEntities = [];
  let ignoreBeforeIndex = 0;

  sortedEntities.forEach((entity) => {
    if (entity.offset >= ignoreBeforeIndex) {
      topLevelEntities.push(entity);
      ignoreBeforeIndex = entity.offset + entity.length;
    }
  });

  return topLevelEntities;
};

const processEntity = (entity, entityText, nestedEntities, checkEmoji, editable, blockedFormats) => {
  const isFormatBlocked = blockedFormats.includes(entity.type);

  const process = () => {
    return processTextWithEntities(entityText, nestedEntities, checkEmoji, editable, blockedFormats);
  };
  switch (entity.type) {
    case TextFormats.BOLD:
      return !isFormatBlocked ? <b>{process()}</b> : process();
    case TextFormats.ITALIC:
      return !isFormatBlocked ? <i>{process()}</i> : process();
    case TextFormats.UNDERLINE:
      return !isFormatBlocked ? <u>{process()}</u> : process();
    case TextFormats.STRIKETHROUGH:
      return !isFormatBlocked ? <del>{process()}</del> : process();
    case TextFormats.CODE:
      return !isFormatBlocked ? <code>{entityText}</code> : entityText;
    case TextFormats.BLOCKQUOTE:
      return !isFormatBlocked ? (
          <blockquote className="relative whitespace-pre-wrap overflow-hidden block w-fit pt-1.5 pb-1 pl-3 pr-6 text-sm rounded-md bg-[#E7F2FC] dark:bg-[#48576D] before:absolute before:top-0 before:left-0 before:bottom-0 before:rounded-l-md before:w-1 before:bg-[#0E7CE0] dark:before:bg-[#1C8EF5] after:absolute after:content-['\201D'] after:top-0 after:right-1 after:text-[#0E7CE0] dark:after:text-[#1C8EF5] after:font-bold after:text-xl">
            {process()}
          </blockquote>
      ) : process();
    case TextFormats.SPOILER:
      return <Spoiler text={process()} editable={editable} clickable={!isFormatBlocked} />;
    case TextFormats.TEXT_LINK:
      return !isFormatBlocked ? <a href={entity.url} className="text-[#0E7CE0] dark:text-[#1C8EF5]">{process()}</a> : entityText;
    default:
      return entityText;
  }
};

const getNestedEntities = (parentEntity, sortedEntities) => {
  return sortedEntities.filter(
    (entity) =>
      !areObjectsEqual(parentEntity, entity) &&
      entity.offset >= parentEntity.offset &&
      entity.offset + entity.length <= parentEntity.offset + parentEntity.length
  );
};

const getEmojiSize = (emoji) => {
  const minEmojiSize = 36;
  const baseSize = 256 * 0.5;
  const matches = emoji.match(emojiRegex());
  const emojiCount = matches.length;
  return emojiCount === 1 ? baseSize : Math.max(baseSize / emojiCount, minEmojiSize);
};

const isOnlyEmoji = (str = "") => {
  const regex = emojiRegex();
  const matches = (str || "").match(regex);
  const cleanedMessage = (str || "")
    .replace(regex, "")
    .replace(/\s+/g, "");

  return matches && cleanedMessage.length === 0;
}

export const processTextWithEntities = (text, entities, checkEmoji = false, editable = true, blockedFormats = []) => {
  let emojiSize = 20;
  let style = {};

  if (checkEmoji && isOnlyEmoji(text)) {
    emojiSize = getEmojiSize(text);

    style = {
      fontSize: `${emojiSize}px`,
      lineHeight: `${emojiSize}px`,
    };
    return <div style={style}>{text}</div>
  }
  if (!entities || !entities.length) {
    return text;
  }
  // 1. Sort entities by their start
  const sortedEntities = entities.sort(
    (e1, e2) => e1.offset - e2.offset || e2.length - e1.length
  );
  // 2. Leave top level entities only
  const topLevelEntities = getTopLevelEntities(sortedEntities);
  // 3. Build result
  let arr = [];
  let index = 0;
  let keyIndex = 0;
  topLevelEntities.forEach((entity) => {
    const entityText = text.substring(
      entity.offset,
      entity.offset + entity.length
    );
    const entityOffset = text.substring(0, entity.offset).length;
    const nestedEntities = getNestedEntities(entity, sortedEntities).map(
      (nestedEntity) => {
        return {
          ...nestedEntity,
          offset: nestedEntity.offset - entityOffset,
        };
      }
    );
    const processedEntity = processEntity(entity, entityText, nestedEntities, checkEmoji, editable, blockedFormats);
    arr.push(<span key={uuidv4()}>{text.substring(index, entity.offset)}</span>);
    arr.push(<span key={uuidv4()}>{processedEntity}</span>);
    index = entity.offset + entity.length;
  });

  if (index < text.length) {
    arr.push(<span key={uuidv4()}>{text.substring(index)}</span>);
  }

  return arr;
};

export const findIntersection = (entity1, entity2, checkNested = true) => {
  if (checkNested && (entity1.offset <= entity2.offset && entity1.offset + entity1.length >= entity2.offset + entity2.length)) {
    return -1;
  }
  const start1 = entity1.offset;
  const end1 = start1 + entity1.length;
  const start2 = entity2.offset;
  const end2 = start2 + entity2.length;

  const maxStart = Math.max(start1, start2);
  const minEnd = Math.min(end1, end2);

  if (maxStart < minEnd) {
      return maxStart;
  } else {
      return -1;
  }
}

const tagMap = {
  b: "bold",
  i: "italic",
  u: "underline",
  del: "strikethrough",
  code: "code",
  a: "text_link",
  blockquote: "blockquote",
  spoiler: "spoiler",
};

export function parseHtmlToEntities(htmlString) {
  const entities = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  function traverse(node, offset, tag) {
    let length = 0;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      length = text.length;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      // if (tagName === 'br') return 1;
      let childLength = 0;
      for (const childNode of node.childNodes) {
        childLength += traverse(childNode, offset + childLength, tagName);
      }
      let type = tagMap[tagName];
      const entity = {
        offset: offset,
        length: childLength,
        type
      };
      if (tagName === "a") {
        entity["url"] = node.href
      } else if (tagName === "span") {
        const classNames = Array.from(node.classList);
        if (classNames.includes("spoiler")) {
          type = "spoiler";
          entity["type"] = type;
        };
      }
      if (type) {
        entities.push(entity);
      }
      length = childLength;
    }
    return length;
  }
  traverse(doc.body, 0);
  return {
    entities: entities.filter(entity => entity.length),
    text: doc.body.textContent
  };
}

const replaceSymbol = (index, text, symbol = "") => text.slice(0, index) + symbol + text.slice(index + 1);

export const fixInnerHtml = (html) => {
  let resultHtml = html;
  let quoteTore = false;
  if (html && html[0] !== "<") {
    let openIndex = html.indexOf("<");
    if (openIndex > -1) {
      resultHtml = `<span>${html.slice(0, openIndex)}</span>${html.slice(openIndex)}`;
    }
  }
  let regex = /<blockquote[^>]*>([\s\S]*?)<\/blockquote>/g;
  let match;
  while ((match = regex.exec(resultHtml)) !== null) {
    let blockquoteContent = match[0];
    if (blockquoteContent.match(/\n{2}<\/blockquote>$/g)) {
      let newBlockquoteContent = blockquoteContent.replace(/\n{2}<\/blockquote>$/g, "</blockquote>");
      resultHtml = resultHtml.replace(blockquoteContent, newBlockquoteContent);

      let blockquoteEndIndex = match.index + newBlockquoteContent.length + "</span>".length;
      resultHtml = resultHtml.slice(0, blockquoteEndIndex) + "<span>\n</span>" + resultHtml.slice(blockquoteEndIndex);
      quoteTore = true;
    }
  }
  return {
    html: resultHtml,
    quoteTore
  };
}

export const replaceCharsWrappedByEntities = (
  text,
  entities,
  options = { symbol: " ", before: true, after: true }
) => {
  const sortedEntities = entities.sort((itemA, itemB) => itemA.offset - itemB.offset);
  let resultText = text;
  let replaceOffset = 0;
  const { symbol, before, after } = options;
  sortedEntities.forEach(entity => {
    if (entity.type !== TextFormats.BLOCKQUOTE) return;
    let beforeIndex = entity.offset + replaceOffset;
    if (before && resultText[beforeIndex] === "\n") {
      resultText = replaceSymbol(beforeIndex, resultText, symbol);
      replaceOffset += symbol.length - 1;
    }
    let afterIndex = entity.offset + entity.length + replaceOffset;
    if (after && resultText[afterIndex] === "\n") {
      resultText = replaceSymbol(afterIndex, resultText, symbol);
      replaceOffset += symbol.length - 1;
    }
  });
  return resultText;
}

export const getModifiedEntities = (newEntity, entitiesArr) => {
  let modifiedEntities = [];
  let overlapping = false;
  let entityExists = false;
  let entitiesMerged = false;
  const sortedEntities = entitiesArr.sort(
    (e1, e2) => e1.offset - e2.offset || e2.length - e1.length
  );
  for (let i = 0; i < sortedEntities.length; i++) {
    const entity = sortedEntities[i];
    const requiresDeselection = !entitiesMerged && (areObjectsEqual(entity, newEntity) || isNewEntityPartOfExisting(entity, newEntity))
    if (requiresDeselection) {
      entityExists = true;
      const firstPartLength = newEntity.offset - entity.offset;
      const secondPartLength = entity.offset + entity.length - (newEntity.offset + newEntity.length);

      if (firstPartLength > 0) {
        modifiedEntities.push({
          ...entity,
          length: firstPartLength
        });
      }

      if (secondPartLength > 0) {
        modifiedEntities.push({
          ...entity,
          offset: newEntity.offset + newEntity.length,
          length: secondPartLength
        });
      }
      continue;
    }
    const requiresModification = isRequiresModification(entity, newEntity)
    if (!requiresDeselection && entityExists && !requiresModification) {
      modifiedEntities.push(entity);
      continue;
    }
    if (requiresModification) {
      const requiresSeparation = isEntityInside(entity, newEntity);
      if (requiresSeparation) {
        entityExists = true;

        const firstPartLength = newEntity.offset - entity.offset;
        if (firstPartLength) {
            modifiedEntities.push({
                ...entity,
                length: firstPartLength
            });
        }

        const secondPartLength = entity.offset + entity.length - newEntity.offset - newEntity.length;
        if (secondPartLength) {
            modifiedEntities.push({
                ...newEntity,
                offset: newEntity.offset + newEntity.length,
                length: secondPartLength
            });
        }
      } else {
        const modifiedEntity = getModifiedEntity(entity, newEntity, entityExists, sortedEntities[i + 1]);
        modifiedEntities.push(modifiedEntity);

        if (!entityExists) {
            entitiesMerged = true;
            newEntity = {
                ...newEntity,
                offset: modifiedEntity.offset + modifiedEntity.length,
                length: Math.abs(newEntity.length - modifiedEntity.length)
            };
        }
      }
      continue;
    }
    const intersection = findIntersection(entity, newEntity);
    if (intersection !== -1) {
      if (newEntity.type === TextFormats.BLOCKQUOTE) {
        if ((entity.offset >= newEntity.offset && entity.offset + entity.length <= newEntity.offset + newEntity.length)) {
          modifiedEntities.push(entity);
          continue;
        } else if (newEntity.offset < entity.offset) {
          modifiedEntities.push({
            type: entity.type,
            offset: entity.offset,
            length: newEntity.offset + newEntity.length - entity.offset,
          });
          modifiedEntities.push({
            type: entity.type,
            offset: newEntity.offset + newEntity.length,
            length: entity.offset + entity.length - (newEntity.offset + newEntity.length),
          });
          continue;
        }
      } if (newEntity.offset > entity.offset) {
        modifiedEntities.push({
          type: entity.type,
          offset: entity.offset,
          length: newEntity.offset - entity.offset,
        });
        modifiedEntities.push({
          type: entity.type,
          offset: newEntity.offset, // intersection,
          length: entity.length - (newEntity.offset - entity.offset),
        });
      } else if (newEntity.offset < entity.offset) {
        overlapping = true;
        modifiedEntities.push(entity);
        modifiedEntities.push({
          type: newEntity.type,
          offset: newEntity.offset,
          length: entity.offset - newEntity.offset,
        });
        modifiedEntities.push({
          type: newEntity.type,
          offset: entity.offset, // intersection,
          length: newEntity.length - (entity.offset - newEntity.offset),
        });
      }
    } else {
      modifiedEntities.push(entity);
    }
  }

  if ((!overlapping && !entityExists && !entitiesMerged) || (entityExists && (newEntity.type === TextFormats.TEXT_LINK || (!entityExists && newEntity.type === TextFormats.BLOCKQUOTE)))) {
    modifiedEntities.push(newEntity);
  }
  return modifiedEntities;
}

export const findLinkedEntity = (selection, entities) => {
  const selectionEntity = {
    type: TextFormats.TEXT_LINK,
    offset: selection.offset,
    length: selection.length,
  };

  for (const entity of entities) {
    if (isNewEntityPartOfExisting(entity, selectionEntity)) {
      return entity;
    }
  }
  return null;
}

export const isSelectionContainsFormatting = (selection, entities) => {
  if (!entities) {
    return false;
  }
  return entities.some(entity => findIntersection(selection, entity, false) !== -1);
}

const isEntityPartOfExisting = (entity1, entity2) => {
  if ((entity1.offset >= entity2.offset && entity1.offset + entity1.length <= entity2.offset + entity2.length)) {
    return true;
  }
  return false;
}

export const onClearSelection = (selection, entities, removeAll) => {
  if (removeAll) {
    return [];
  }
  return entities.flatMap(entity => {
    const intersection = findIntersection(selection, entity, false);
    if (intersection === -1) {
      return [entity];
    } else if (isEntityPartOfExisting(entity, selection)) {
      return [];
    } else {
      const newEntities = [];
      if (entity.offset < selection.offset) {
        newEntities.push({
          ...entity,
          length: selection.offset - entity.offset
        });
      }
      if (entity.offset + entity.length > selection.offset + selection.length) {
        newEntities.push({
          ...entity,
          offset: selection.offset + selection.length,
          length: (entity.offset + entity.length) - (selection.offset + selection.length)
        });
      }
      return newEntities;
    }
  });
};
